import React, { useState, useCallback } from 'react';
import { Code, Copy, Check, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

// CodeBlock component defined outside to avoid recreation on render
const CodeBlock = ({ code, id, copiedCode, onCopy }) => (
  <div className="relative mt-4">
    <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
      <code className="text-sm text-gray-300 font-mono whitespace-pre-wrap">{code}</code>
    </pre>
    <button
      onClick={() => onCopy(code, id)}
      className="absolute top-2 right-2 p-2 rounded bg-gray-800 hover:bg-gray-700 transition-colors"
    >
      {copiedCode === id ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-gray-400" />
      )}
    </button>
  </div>
);

const ApiDocs = () => {
  const [expandedSection, setExpandedSection] = useState('getting-started');
  const [copiedCode, setCopiedCode] = useState(null);

  const copyCode = useCallback((code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  }, []);

  const methodColors = {
    GET: 'bg-green-500/20 text-green-400',
    POST: 'bg-blue-500/20 text-blue-400',
    PUT: 'bg-yellow-500/20 text-yellow-400',
    DELETE: 'bg-red-500/20 text-red-400'
  };

  const endpoints = [
    {
      id: 'artists',
      title: 'Artists',
      items: [
        {
          method: 'GET',
          path: '/api/artists',
          description: 'Get all approved artists with optional filters',
          auth: false,
          params: ['genres', 'min_price', 'max_price', 'min_rating', 'is_local', 'available_date'],
          response: '{\n  "artists": [\n    {\n      "artist_id": "artist_123",\n      "bio": "Professional DJ",\n      "genres": ["electronic"],\n      "hourly_rate": 150.00,\n      "rating": 4.8\n    }\n  ]\n}'
        },
        {
          method: 'GET',
          path: '/api/artists/{artist_id}',
          description: 'Get a specific artist by ID',
          auth: false
        }
      ]
    },
    {
      id: 'bookings',
      title: 'Bookings',
      items: [
        {
          method: 'POST',
          path: '/api/bookings',
          description: 'Create a new booking',
          auth: true,
          body: '{\n  "artist_id": "artist_123",\n  "event_date": "2024-12-25",\n  "event_duration": 4,\n  "is_local": true\n}'
        },
        {
          method: 'GET',
          path: '/api/bookings',
          description: 'Get all bookings for authenticated user',
          auth: true
        }
      ]
    },
    {
      id: 'reviews',
      title: 'Reviews',
      items: [
        {
          method: 'GET',
          path: '/api/artists/{artist_id}/reviews',
          description: 'Get all reviews for an artist',
          auth: false
        },
        {
          method: 'POST',
          path: '/api/reviews',
          description: 'Submit a review for a completed booking',
          auth: true
        }
      ]
    },
    {
      id: 'events',
      title: 'Events',
      items: [
        {
          method: 'GET',
          path: '/api/events',
          description: 'Get public events',
          auth: false
        },
        {
          method: 'POST',
          path: '/api/events',
          description: 'Create a new event',
          auth: true
        }
      ]
    },
    {
      id: 'loyalty',
      title: 'Loyalty',
      items: [
        {
          method: 'GET',
          path: '/api/loyalty',
          description: 'Get user loyalty status and points',
          auth: true
        },
        {
          method: 'POST',
          path: '/api/loyalty/redeem',
          description: 'Redeem loyalty points for discount',
          auth: true
        }
      ]
    }
  ];

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Code className="w-10 h-10 text-purple-400" />
            <h1 className="text-4xl font-bold text-white">API Documentation</h1>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Integrate RazientSounds into your application with our comprehensive REST API
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="sticky top-24 bg-white/5 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4">API Reference</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setExpandedSection('getting-started')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      expandedSection === 'getting-started'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    Getting Started
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setExpandedSection('rate-limits')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      expandedSection === 'rate-limits'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    Rate Limits
                  </button>
                </li>
                {endpoints.map(section => (
                  <li key={section.id}>
                    <button
                      onClick={() => setExpandedSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        expandedSection === section.id
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'text-gray-300 hover:bg-white/5'
                      }`}
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Getting Started */}
            {expandedSection === 'getting-started' && (
              <div className="bg-white/5 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Getting Started</h2>
                <div className="space-y-4">
                  <p className="text-gray-300">
                    Welcome to the RazientSounds API! Our API allows you to programmatically access artist data, 
                    manage bookings, and integrate our platform into your applications.
                  </p>
                  <h3 className="text-lg font-semibold text-white">Base URL</h3>
                  <CodeBlock code="https://api.razientsounds.com/api" id="base-url" copiedCode={copiedCode} onCopy={copyCode} />
                  <h3 className="text-lg font-semibold text-white">Authentication</h3>
                  <p className="text-gray-300">
                    All API requests require authentication. Include your API key in the Authorization header:
                  </p>
                  <CodeBlock 
                    code={'curl -X GET "https://api.razientsounds.com/api/artists" \\\n  -H "Authorization: Bearer YOUR_API_KEY"'}
                    id="auth-example"
                    copiedCode={copiedCode}
                    onCopy={copyCode}
                  />
                </div>
              </div>
            )}

            {/* Rate Limits */}
            {expandedSection === 'rate-limits' && (
              <div className="bg-white/5 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Rate Limits</h2>
                <div className="space-y-4">
                  <p className="text-gray-300">
                    To ensure fair usage and platform stability, API requests are rate limited:
                  </p>
                  <div className="bg-gray-800 rounded-lg p-4 overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-2 text-gray-400">Plan</th>
                          <th className="text-left py-2 text-gray-400">Requests/minute</th>
                          <th className="text-left py-2 text-gray-400">Requests/day</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-700">
                          <td className="py-2 text-white">Free</td>
                          <td className="py-2 text-white">60</td>
                          <td className="py-2 text-white">1,000</td>
                        </tr>
                        <tr className="border-b border-gray-700">
                          <td className="py-2 text-white">Basic</td>
                          <td className="py-2 text-white">200</td>
                          <td className="py-2 text-white">10,000</td>
                        </tr>
                        <tr className="border-b border-gray-700">
                          <td className="py-2 text-white">Premium</td>
                          <td className="py-2 text-white">500</td>
                          <td className="py-2 text-white">50,000</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-white">Enterprise</td>
                          <td className="py-2 text-white">Unlimited</td>
                          <td className="py-2 text-white">Unlimited</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Rate limit headers are included in every response: X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset
                  </p>
                </div>
              </div>
            )}

            {/* API Endpoints */}
            {endpoints.map(section => (
              expandedSection === section.id && (
                <div key={section.id} className="bg-white/5 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                  <div className="space-y-6">
                    {section.items.map((endpoint, i) => (
                      <div key={i} className="border-b border-gray-700 pb-6 last:border-0">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${methodColors[endpoint.method]}`}>
                            {endpoint.method}
                          </span>
                          <code className="text-purple-400 font-mono">{endpoint.path}</code>
                          {endpoint.auth ? (
                            <Lock className="w-4 h-4 text-yellow-400" title="Authentication required" />
                          ) : (
                            <Unlock className="w-4 h-4 text-green-400" title="Public endpoint" />
                          )}
                        </div>
                        <p className="text-gray-300 mb-4">{endpoint.description}</p>
                        
                        {endpoint.params && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Query Parameters</h4>
                            <div className="flex flex-wrap gap-2">
                              {endpoint.params.map(param => (
                                <code key={param} className="text-xs px-2 py-1 bg-gray-800 rounded text-gray-300">
                                  {param}
                                </code>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {endpoint.body && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Request Body</h4>
                            <CodeBlock code={endpoint.body} id={`body-${section.id}-${i}`} copiedCode={copiedCode} onCopy={copyCode} />
                          </div>
                        )}
                        
                        {endpoint.response && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Response</h4>
                            <CodeBlock code={endpoint.response} id={`response-${section.id}-${i}`} copiedCode={copiedCode} onCopy={copyCode} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;
